import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QRCode from 'qrcode';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';
import { toast } from 'react-toastify';
import BrowserPrintService from '../services/BrowserPrintService';

const parseItemCustomizations = (item) => {
  if (Array.isArray(item?.customizations)) return item.customizations;
  try { return JSON.parse(item?.customizations || '[]'); } catch (_) { return []; }
};

const itemGroupingKey = (item) => JSON.stringify({
  product: item?.produto_id || item?.product_id || item?.produto_name || item?.product_name,
  size: item?.size_label || item?.size || '',
  observation: String(item?.observation || '').trim().toLowerCase(),
  options: (item?.options || []).map((option) => option?.id || option?.name).filter(Boolean).sort(),
  customizations: parseItemCustomizations(item).map((group) => ({
    group: group?.group_id || group?.group_name || group?.groupName,
    options: (group?.selected_options || group?.selectedOptions || [])
      .map((option) => option?.id || option?.name)
      .filter(Boolean)
      .sort()
  }))
});

const groupOrderItems = (order) => {
  const groups = new Map();
  (order?.items || order?.itens || []).forEach((item) => {
    const key = itemGroupingKey(item);
    const current = groups.get(key);
    if (current) {
      current.quantity += Number(item.quantity || 1);
      current.subtotal += Number(item.subtotal || 0);
      current._sourceItems.push(item);
    } else {
      groups.set(key, {
        ...item,
        quantity: Number(item.quantity || 1),
        subtotal: Number(item.subtotal || 0),
        _sourceItems: [item]
      });
    }
  });
  return Array.from(groups.values());
};

function MesasManagement() {
  const { user } = useAuth();
  const [mesas, setMesas] = useState([]);
  const [lanchonete, setLanchonete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMesa, setSelectedMesa] = useState(null);
  const [mesaDetails, setMesaDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [qrMesa, setQrMesa] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedSize, setSelectedSize] = useState('');
  const [productQuantity, setProductQuantity] = useState(1);
  const [productObservation, setProductObservation] = useState('');
  const [savingProduct, setSavingProduct] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  const mesasRequestInFlight = useRef(false);

  const loadLanchonete = useCallback(async () => {
    if (!user?.lanchonete_id) return;
    try {
      const response = await api.get(`/lanchonetes/${user.lanchonete_id}`);
      setLanchonete(response.data);
    } catch (error) {
      console.error('Erro ao carregar lanchonete:', error);
    }
  }, [user?.lanchonete_id]);

  const loadMesas = useCallback(async () => {
    if (mesasRequestInFlight.current) return;
    mesasRequestInFlight.current = true;
    try {
      setLoading(true);
      const response = await api.get('/mesas');
      setMesas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar mesas');
    } finally {
      mesasRequestInFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMesas();
    loadLanchonete();
  }, [loadMesas, loadLanchonete]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (!document.hidden) loadMesas();
    };
    const refreshInterval = window.setInterval(refreshIfVisible, 60 * 1000);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      window.clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [loadMesas]);

  const handleOpenDetails = async (mesa) => {
    setSelectedMesa(mesa);
    setMesaDetails(null);
    if (mesa.status === 'disponivel') return;

    try {
      setDetailsLoading(true);
      const response = await api.get(`/mesas/${mesa.id}/details`);
      setMesaDetails(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da mesa');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePrintMesa = async () => {
    if (!selectedMesa || !mesaDetails || !lanchonete) {
      toast.error('Dados insuficientes para impressão');
      return;
    }

    const success = await BrowserPrintService.imprimirContaMesa(selectedMesa, mesaDetails, lanchonete);
    if (success) {
      toast.success('🖨️ Conta enviada para impressão');
    } else {
      toast.error('❌ Falha ao imprimir conta');
    }
  };

  const handlePrintKitchen = async () => {
    if (!selectedMesa || !mesaDetails || !lanchonete) {
      toast.error('Dados insuficientes para imprimir a cozinha');
      return;
    }
    const result = await BrowserPrintService.imprimirCozinhaMesa(selectedMesa, mesaDetails, lanchonete);
    if (result.reason === 'no_kitchen_items') {
      toast.info('Esta mesa nao possui itens de cozinha para imprimir.');
    } else if (result.success) {
      toast.success('Pedido da cozinha enviado para impressao');
    } else {
      toast.error('Falha ao imprimir o pedido da cozinha');
    }
  };

  const handleOpenPayment = () => {
    setPaymentAmount('');
    setPaymentDialogOpen(true);
  };

  const handlePartialPayment = async () => {
    const amount = Number(String(paymentAmount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Informe um valor maior que zero');
      return;
    }

    try {
      setSavingPayment(true);
      const response = await api.post(`/mesas/${selectedMesa.id}/partial-payment`, {
        amount
      });
      setMesaDetails(response.data);
      setPaymentDialogOpen(false);
      setPaymentAmount('');
      toast.success('Pagamento parcial registrado. A mesa continua aberta.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar pagamento parcial');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleCloseMesa = async (mesaId) => {
    if (window.confirm('Deseja realmente fechar esta mesa e liberar para novos clientes?')) {
      try {
        await api.post(`/mesas/${mesaId}/close`);
        toast.success('Mesa liberada com sucesso!');
        setSelectedMesa(null);
        loadMesas();
      } catch (error) {
        toast.error('Erro ao fechar mesa');
      }
    }
  };

  const getMesaQrUrl = (mesa) => `${window.location.origin}/app/mesa/${mesa.qr_token}`;

  const handleOpenQr = async (event, mesa) => {
    event.stopPropagation();
    try {
      setQrLoading(true);
      setQrMesa(mesa);
      setQrDataUrl(await QRCode.toDataURL(getMesaQrUrl(mesa), {
        width: 420,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: { dark: '#111827', light: '#FFFFFF' }
      }));
    } catch (error) {
      toast.error('Erro ao gerar QR Code da mesa');
      setQrMesa(null);
    } finally {
      setQrLoading(false);
    }
  };

  const handlePrintQr = async () => {
    if (!qrMesa || !qrDataUrl) return;
    await BrowserPrintService.imprimirHtml(
      `QR Code Mesa ${qrMesa.numero}`,
      `<div style="width: 320px; text-align: center; font-family: Arial, sans-serif; color: #000; padding: 20px;">
        <h2 style="margin: 0 0 6px;">${lanchonete?.name || 'Lanchonete'}</h2>
        <h1 style="font-size: 34px; margin: 0 0 12px;">MESA ${qrMesa.numero}</h1>
        <img src="${qrDataUrl}" alt="QR Code da mesa" style="width: 280px; height: 280px;" />
        <p style="font-size: 18px; font-weight: bold; margin: 12px 0 0;">Leia o QR Code para abrir o cardapio e fazer seu pedido</p>
      </div>`
    );
  };

  const handleCopyQrLink = async () => {
    if (!qrMesa) return;
    try {
      await navigator.clipboard.writeText(getMesaQrUrl(qrMesa));
      toast.success('Link da mesa copiado');
    } catch (_) {
      toast.error('Nao foi possivel copiar o link');
    }
  };

  const handleRegenerateQr = async () => {
    if (!qrMesa || !window.confirm('Gerar um novo QR Code? O codigo impresso anteriormente deixara de funcionar.')) return;
    try {
      setQrLoading(true);
      const response = await api.post(`/mesas/${qrMesa.id}/regenerate-qr`);
      const updatedMesa = response.data;
      setMesas((current) => current.map((mesa) => mesa.id === updatedMesa.id ? { ...mesa, ...updatedMesa } : mesa));
      setQrMesa(updatedMesa);
      setQrDataUrl(await QRCode.toDataURL(getMesaQrUrl(updatedMesa), { width: 420, margin: 2, errorCorrectionLevel: 'H' }));
      toast.success('Novo QR Code gerado');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar novo QR Code');
    } finally {
      setQrLoading(false);
    }
  };

  const openProductDialog = async () => {
    try {
      setProductDialogOpen(true);
      setSelectedProduct(null);
      setProductSearch('');
      const response = await api.get(`/produtos?lanchonete_id=${user.lanchonete_id}&available=true`);
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Erro ao carregar o cardapio');
    }
  };

  const chooseProduct = async (product) => {
    setSelectedProduct(product);
    setSelectedOptions({});
    setSelectedSize(Number(product.price_p) > 0 ? 'P' : Number(product.price_g) > 0 ? 'G' : '');
    setProductQuantity(1);
    setProductObservation('');
    const response = await api.get(`/product-options/groups/product/${product.id}`).catch(() => ({ data: [] }));
    setProductOptions(Array.isArray(response.data) ? response.data : []);
  };

  const toggleProductOption = (group, option) => {
    setSelectedOptions((current) => {
      const currentGroup = current[group.id] || [];
      const exists = currentGroup.some((item) => Number(item.id) === Number(option.id));
      let nextGroup = exists
        ? currentGroup.filter((item) => Number(item.id) !== Number(option.id))
        : [...currentGroup, option];
      if (!exists && (group.type === 'single' || Number(group.max_selections || 99) === 1)) nextGroup = [option];
      if (nextGroup.length > Number(group.max_selections || 99)) {
        toast.warning(`Escolha no maximo ${group.max_selections} opcao(oes) em ${group.name}`);
        return current;
      }
      return { ...current, [group.id]: nextGroup };
    });
  };

  const addProductToMesa = async () => {
    if (!selectedProduct || !selectedMesa) return;
    const hasSizes = Number(selectedProduct.price_p) > 0 || Number(selectedProduct.price_g) > 0;
    if (hasSizes && !selectedSize) return toast.warning('Escolha o tamanho do produto');
    for (const group of productOptions) {
      if (group.required && (selectedOptions[group.id] || []).length < Number(group.min_selections || 1)) {
        return toast.warning(`Escolha as opcoes obrigatorias em ${group.name}`);
      }
    }
    const customizations = productOptions
      .map((group) => ({ groupId: group.id, groupName: group.name, selectedOptions: selectedOptions[group.id] || [] }))
      .filter((group) => group.selectedOptions.length > 0);
    if (selectedSize) {
      customizations.unshift({
        groupId: 'pizza-size',
        groupName: 'Tamanho',
        selectedOptions: [{ id: `pizza-${selectedSize}`, name: `Tamanho ${selectedSize}`, price: 0 }]
      });
    }
    try {
      setSavingProduct(true);
      await api.post(`/mesas/${selectedMesa.id}/pedido`, {
        customer_name: selectedMesa.customer_name || 'Cliente Mesa',
        payment_method: 'mesa',
        items: [{
          produto_id: selectedProduct.id,
          quantity: productQuantity,
          size_label: selectedSize || null,
          observation: productObservation,
          customizations
        }]
      }, { headers: { 'Idempotency-Key': `admin-mesa-${selectedMesa.id}-${Date.now()}` } });
      const response = await api.get(`/mesas/${selectedMesa.id}/details`);
      setMesaDetails(response.data);
      setProductDialogOpen(false);
      toast.success('Produto adicionado e enviado para preparo');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao adicionar produto');
    } finally {
      setSavingProduct(false);
    }
  };

  const removeItemFromMesa = async (order, item) => {
    const quantity = Number(item.quantity || 1);
    const removeAll = quantity <= 1 || window.confirm(`Retirar todas as ${quantity} unidades deste item? Clique em Cancelar para retirar somente 1.`);
    if (!window.confirm(`Confirma a retirada de ${removeAll ? quantity : 1}x ${item.produto_name || item.product_name}?`)) return;
    try {
      setRemovingItemId(item.id);
      const sources = Array.isArray(item._sourceItems) && item._sourceItems.length > 0
        ? item._sourceItems
        : [item];
      const itemsToRemove = removeAll ? sources : [sources[0]];
      let latestDetails = null;
      for (const source of itemsToRemove) {
        const response = await api.delete(`/mesas/${selectedMesa.id}/orders/${order.id}/items/${source.id}`, {
          data: { quantity: removeAll ? Number(source.quantity || 1) : 1 }
        });
        latestDetails = response.data;
      }
      if (latestDetails) setMesaDetails(latestDetails);
      toast.success('Item retirado e estoque ajustado');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao retirar item');
    } finally {
      setRemovingItemId(null);
    }
  };

  if (loading && mesas.length === 0) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" fontWeight="bold" display="flex" alignItems="center" gap={2}>
          <RestaurantIcon fontSize="large" color="primary" />
          Gestão de Mesas (1 a 50)
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadMesas} variant="outlined">Atualizar</Button>
      </Box>

      <Grid container spacing={2}>
        {mesas.map((mesa) => (
          <Grid item xs={6} sm={4} md={2.4} lg={1.2} key={mesa.id}>
            <Paper
              elevation={3}
              onClick={() => handleOpenDetails(mesa)}
              sx={{
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                bgcolor: mesa.status === 'disponivel' ? 'success.light' : 'error.light',
                color: 'white',
                borderRadius: 2,
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 6
                }
              }}
            >
              <Typography variant="h5" fontWeight="bold">#{mesa.numero}</Typography>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                {mesa.status === 'disponivel' ? 'Livre' : 'Ocupada'}
              </Typography>
              {mesa.customer_name && (
                <Typography variant="body2" noWrap sx={{ mt: 1, fontSize: '0.75rem' }}>
                  👤 {mesa.customer_name}
                </Typography>
              )}
              <IconButton
                size="small"
                title={`QR Code da Mesa ${mesa.numero}`}
                onClick={(event) => handleOpenQr(event, mesa)}
                sx={{ mt: 1, color: 'white', bgcolor: 'rgba(0,0,0,0.18)', '&:hover': { bgcolor: 'rgba(0,0,0,0.3)' } }}
              >
                <QrCode2Icon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(selectedMesa)} onClose={() => setSelectedMesa(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: selectedMesa?.status === 'disponivel' ? 'success.main' : 'error.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Mesa #{selectedMesa?.numero}
          <IconButton onClick={() => setSelectedMesa(null)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedMesa?.status === 'disponivel' ? (
            <Box textAlign="center" p={3}>
              <RestaurantIcon sx={{ fontSize: 60, color: 'success.main', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6">Mesa disponível para atendimento.</Typography>
              <Typography color="textSecondary">O garçom pode abrir esta mesa pelo aplicativo.</Typography>
            </Box>
          ) : (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={2} gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Cliente</Typography>
                  <Typography variant="h6">{selectedMesa?.customer_name}</Typography>
                </Box>
                <Box textAlign="right" display="flex" flexWrap="wrap" justifyContent="flex-end" gap={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Consumido</Typography>
                    <Typography variant="h6" fontWeight="bold">R$ {Number(mesaDetails?.total_parcial || 0).toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Ja pago</Typography>
                    <Typography variant="h6" color="success.main" fontWeight="bold">R$ {Number(mesaDetails?.total_paid || 0).toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Saldo</Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">R$ {Number(mesaDetails?.balance_due || 0).toFixed(2)}</Typography>
                  </Box>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>Pedidos na Mesa</Typography>
              {detailsLoading ? (
                <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
              ) : (
                <List>
                  {mesaDetails?.orders?.length === 0 ? (
                    <Typography color="textSecondary">Nenhum pedido lançado ainda.</Typography>
                  ) : (
                    mesaDetails?.orders.map((order) => (
                      <ListItem key={order.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" gap={2}>
                              <Typography fontWeight="bold">Pedido #{order.id}</Typography>
                              <Typography fontWeight="bold">R$ {Number(order.total).toFixed(2)}</Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(order.created_at).toLocaleTimeString()}
                                </Typography>
                                <Chip label={order.status} size="small" color="primary" variant="outlined" />
                              </Box>

                              {groupOrderItems(order).map((item) => (
                                <Box key={item.id} sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" color="text.primary" fontWeight="600">
                                    {item.quantity}x {item.produto_name || item.product_name || `Produto #${item.produto_id}`}
                                  </Typography>
                                  {Array.isArray(item.options) && item.options.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Adicionais: {item.options.map((option) => option.name).filter(Boolean).join(', ')}
                                    </Typography>
                                  )}
                                  {(Array.isArray(item.customizations)
                                    ? item.customizations
                                    : (() => {
                                        try { return JSON.parse(item.customizations || '[]'); } catch (_) { return []; }
                                      })()
                                  ).map((group, index) => {
                                    const names = (group.selected_options || group.selectedOptions || [])
                                      .map((option) => option.name)
                                      .filter(Boolean);
                                    return names.length > 0 ? (
                                      <Typography
                                        key={`${item.id}-customization-${index}`}
                                        variant="caption"
                                        color="success.main"
                                        fontWeight="700"
                                        display="block"
                                      >
                                        {group.group_name || group.groupName || 'Opcoes'}: {names.join(', ')}
                                      </Typography>
                                    ) : null;
                                  })}
                                  {item.observation ? (
                                    <Typography variant="caption" color="warning.main" display="block">
                                      Obs: {item.observation}
                                    </Typography>
                                  ) : null}
                                  </Box>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    title="Diminuir ou retirar item"
                                    disabled={removingItemId === item.id || Number(mesaDetails?.total_paid || 0) > 0}
                                    onClick={() => removeItemFromMesa(order, item)}
                                  >
                                    {removingItemId === item.id ? <CircularProgress size={18} /> : <RemoveCircleOutlineIcon />}
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              )}

              {Array.isArray(mesaDetails?.payments) && mesaDetails.payments.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" mb={1}>Pagamentos recebidos</Typography>
                  <List dense disablePadding>
                    {mesaDetails.payments.map((payment) => (
                      <ListItem key={payment.id} disableGutters>
                        <ListItemText
                          primary={`Valor recebido: R$ ${Number(payment.amount || 0).toFixed(2)}`}
                          secondary={`${new Date(payment.created_at).toLocaleString('pt-BR')}${payment.received_by_name ? ` - Recebido por ${payment.received_by_name}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelectedMesa(null)}>Fechar</Button>
          {selectedMesa?.status === 'ocupada' && (
            <>
              <Button variant="contained" startIcon={<AddShoppingCartIcon />} onClick={openProductDialog}>
                Adicionar lanche
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentsIcon />}
                onClick={handleOpenPayment}
                disabled={detailsLoading || Number(mesaDetails?.balance_due || 0) <= 0}
              >
                Receber valor parcial
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<PrintIcon />}
                onClick={handlePrintMesa}
                disabled={detailsLoading}
              >
                Imprimir Conta
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<RestaurantMenuIcon />}
                onClick={handlePrintKitchen}
                disabled={detailsLoading}
              >
                Cozinha
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => handleCloseMesa(selectedMesa.id)}
              >
                Fechar Conta e Liberar Mesa
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={productDialogOpen} onClose={() => !savingProduct && setProductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedProduct ? selectedProduct.name : `Adicionar na Mesa #${selectedMesa?.numero}`}</DialogTitle>
        <DialogContent dividers>
          {!selectedProduct ? (
            <>
              <TextField fullWidth autoFocus label="Pesquisar produto" value={productSearch} onChange={(event) => setProductSearch(event.target.value)} sx={{ mb: 2 }} />
              <List>
                {products
                  .filter((product) => String(product.name || '').toLowerCase().includes(productSearch.trim().toLowerCase()))
                  .map((product) => (
                    <ListItem key={product.id} button onClick={() => chooseProduct(product)} divider>
                      <ListItemText
                        primary={product.name}
                        secondary={Number(product.price_p) > 0 || Number(product.price_g) > 0
                          ? `P: R$ ${Number(product.price_p || 0).toFixed(2)} | G: R$ ${Number(product.price_g || 0).toFixed(2)}`
                          : `R$ ${Number(product.price || 0).toFixed(2)}`}
                      />
                    </ListItem>
                  ))}
              </List>
            </>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {(Number(selectedProduct.price_p) > 0 || Number(selectedProduct.price_g) > 0) && (
                <Box>
                  <Typography fontWeight="bold" mb={1}>Tamanho</Typography>
                  {Number(selectedProduct.price_p) > 0 && <Button variant={selectedSize === 'P' ? 'contained' : 'outlined'} onClick={() => setSelectedSize('P')} sx={{ mr: 1 }}>P - R$ {Number(selectedProduct.price_p).toFixed(2)}</Button>}
                  {Number(selectedProduct.price_g) > 0 && <Button variant={selectedSize === 'G' ? 'contained' : 'outlined'} onClick={() => setSelectedSize('G')}>G - R$ {Number(selectedProduct.price_g).toFixed(2)}</Button>}
                </Box>
              )}
              {productOptions.map((group) => (
                <Box key={group.id}>
                  <Typography fontWeight="bold">{group.name}{group.required ? ' *' : ''}</Typography>
                  {(group.options || []).map((option) => (
                    <FormControlLabel
                      key={option.id}
                      control={<Checkbox checked={(selectedOptions[group.id] || []).some((item) => Number(item.id) === Number(option.id))} onChange={() => toggleProductOption(group, option)} />}
                      label={`${option.name}${Number(option.price) > 0 ? ` (+ R$ ${Number(option.price).toFixed(2)})` : ''}`}
                    />
                  ))}
                </Box>
              ))}
              <TextField label="Quantidade" type="number" value={productQuantity} onChange={(event) => setProductQuantity(Math.max(1, Math.min(99, Number(event.target.value) || 1)))} inputProps={{ min: 1, max: 99 }} />
              <TextField label="Observacao" value={productObservation} onChange={(event) => setProductObservation(event.target.value)} multiline minRows={2} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedProduct && <Button onClick={() => setSelectedProduct(null)}>Voltar ao cardapio</Button>}
          <Button onClick={() => setProductDialogOpen(false)} disabled={savingProduct}>Cancelar</Button>
          {selectedProduct && <Button variant="contained" onClick={addProductToMesa} disabled={savingProduct}>{savingProduct ? 'Adicionando...' : 'Adicionar e imprimir'}</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={() => !savingPayment && setPaymentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Receber valor parcial - Mesa #{selectedMesa?.numero}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography color="text.secondary" mb={2}>
            Saldo atual: <strong>R$ {Number(mesaDetails?.balance_due || 0).toFixed(2)}</strong>. A mesa permanecera aberta.
          </Typography>
          <TextField
            label="Valor recebido"
            value={paymentAmount}
            onChange={(event) => setPaymentAmount(event.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
            autoFocus
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} disabled={savingPayment}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handlePartialPayment} disabled={savingPayment}>
            {savingPayment ? 'Registrando...' : 'Confirmar recebimento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(qrMesa)} onClose={() => !qrLoading && setQrMesa(null)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code - Mesa #{qrMesa?.numero}</DialogTitle>
        <DialogContent dividers sx={{ textAlign: 'center' }}>
          {qrLoading && !qrDataUrl ? <CircularProgress /> : (
            <>
              <Typography fontWeight={800}>{lanchonete?.name}</Typography>
              {qrDataUrl && <Box component="img" src={qrDataUrl} alt={`QR Code da Mesa ${qrMesa?.numero}`} sx={{ width: '100%', maxWidth: 320, my: 2 }} />}
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {qrMesa ? getMesaQrUrl(qrMesa) : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                Cada mesa possui um codigo exclusivo desta lanchonete.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopyQrLink}>Copiar link</Button>
          <Button color="warning" onClick={handleRegenerateQr} disabled={qrLoading}>Gerar novo</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintQr} disabled={!qrDataUrl || qrLoading}>Imprimir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MesasManagement;
